import {useContext, useEffect} from "react";
import {ThemeProvider} from "react-bootstrap";
import {BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import {ToastContainer} from 'react-toastify';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { useTranslation } from "react-i18next";
import FormLayout from "./views/FormLayout";
import EntryForm from "./views/EntryForm";
import UTMPage from "./views/UTMPage";
import KitchenSink from "./views/KitchenSink";
import NotFound from "./views/NotFound";
import FontSelector from "./components/FontSelector";
import AppConfigContext from "./context/AppConfigContext";
import APP_ENV from "./config/appEnv";
import './App.scss';
import { LANG_PARAM_MAP, I18N_TO_URL_LANG, TRACK_BASE_URLS, PROGRAM_TYPE } from "./utils/constants";
import { getProgramTypeFromSubdomain } from "./utils/utils";

if (APP_ENV.APP_INSIGHT_CONNECTION_STRING) {
    const appInsights = new ApplicationInsights({ config: {
        connectionString: APP_ENV.APP_INSIGHT_CONNECTION_STRING
    } });

    appInsights.loadAppInsights();
    window.appInsights = appInsights;
    appInsights.trackPageView();

    var urlParams = new URLSearchParams(window.location.search);
    var utmSource = urlParams.get('utm_source');
    var utmMedium = urlParams.get('utm_medium');
    var utmCampaign = urlParams.get('utm_campaign');
    var utmState = urlParams.get('utm_state');
    var utmTenant = urlParams.get('utm_tenant');

    if (utmSource && utmMedium && utmCampaign) {
        appInsights.trackEvent({ name: 'UTM Parameters', properties: { 
            utmSource: utmSource, 
            utmMedium: utmMedium, 
            utmCampaign: utmCampaign,
            utmState: utmState,
            utmTenant: utmTenant
        }});
    } else {
        // specific clients default to n/a if no utm data is provided but still wants to be tracked
        if (TRACK_BASE_URLS.includes(APP_ENV.CLIENT_SHORT_NAME)) {
            appInsights.trackEvent({ name: 'UTM Parameters', properties: { 
                utmSource: "n/a", 
                utmMedium: "n/a", 
                utmCampaign: "n/a",
                utmState: utmState,
                utmTenant: utmTenant
            }});
        }
    }
}

// ---------- Sub-component to handle routing and language sync ----------
const AppWithRoutes = () => {
    const { config } = useContext(AppConfigContext);
    const { i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        document.body.classList.add(`theme-${config.theme}`);

        const urlParams = new URLSearchParams(location.search);
        const langParam = urlParams.get('lang');
        // The C-SNP host carries English-only copy, so a language parameter cannot switch it.
        const englishOnly = getProgramTypeFromSubdomain() === PROGRAM_TYPE.CSNP;
        const langCode = !englishOnly && langParam && LANG_PARAM_MAP[langParam] ? LANG_PARAM_MAP[langParam] : LANG_PARAM_MAP.eng;

        if (i18n.language !== langCode) {
            i18n.changeLanguage(langCode);
            localStorage.setItem('locale', langCode);
        }

        // If lang param is missing and language is not English, update the URL
        if (!langParam && langCode !== LANG_PARAM_MAP.eng) {
            urlParams.set('lang', I18N_TO_URL_LANG[langCode]);
            navigate({ search: urlParams.toString() }, { replace: true });
        }

        // If lang param is 'eng' (default), clean it from URL
        if (langParam === I18N_TO_URL_LANG.en) {
            urlParams.delete('lang');
            navigate({ search: urlParams.toString() }, { replace: true });
        }
    }, [config.theme, location.search, navigate, i18n]);

    const formChildren = (
        <>
            <Route index element={<EntryForm />} />
            <Route path="apply" element={<EntryForm />} />
            <Route path="u/:source" element={<UTMPage />} />
            <Route path="*" element={<NotFound />} />
        </>
    );

    return (
        <Routes>
            <Route element={<FormLayout />}>
                {formChildren}
            </Route>
            <Route path="/temp" element={<KitchenSink />} />
        </Routes>
    );
};

// ---------- Main App ----------
function App() {
    const {config} = useContext(AppConfigContext);

    useEffect(() => {
        document.body.classList.add(`theme-${config.theme}`);
    }, [config.theme]);

    return (
        <ThemeProvider
            breakpoints={['lg', 'md', 'sm', 'xs']}
        >
            <FontSelector theme={config.theme}>
                <div className="app">
                    <ToastContainer
                        position="bottom-right"
                        closeButton={true}
                        autoClose={3000}
                    />
                    <Router>
                        <Routes>
                            <Route path="/*" element={<AppWithRoutes />} />
                        </Routes>
                    </Router>
                </div>
            </FontSelector>
        </ThemeProvider>
    );
}

export default App;
